import api from "./axios";

// Tiny helper — every list endpoint accepts ?page= and ?search=
const listUrl = (path, page = 1, opts = {}) => {
  const q = new URLSearchParams({ page: String(page) });
  if (opts.search) q.set("search", opts.search);
  return `${path}?${q.toString()}`;
};

// PLAYERS — 2026-06-12: forward owner_type / nationality / has_ci_id
// from the current URL so links like
//   /cricket/players?owner_type=SYSTEM&nationality=NZL&has_ci_id=true
// filter server-side without ListTemplate needing to know about the
// params. Other listUrl callers ignore unknown opts so this stays
// self-contained.
const _PASSTHROUGH = ["owner_type", "nationality", "gender", "has_ci_id", "all_tenants", "owner_id", "ci_player_id"];
export const listPlayers = (page = 1, opts = {}) => {
  const base = listUrl(`/api/coredata/players/`, page, opts);
  let extra = "";
  if (typeof window !== "undefined") {
    const url = new URLSearchParams(window.location.search);
    for (const key of _PASSTHROUGH) {
      const v = url.get(key);
      if (v) extra += `&${key}=${encodeURIComponent(v)}`;
    }
  }
  return api.get(base + extra);
};
export const getPlayer = (id) => api.get(`/api/coredata/players/${id}/`);
export const createPlayer = (payload) =>
  api.post(`/api/coredata/players/`, payload);
export const updatePlayer = (id, payload) =>
  api.put(`/api/coredata/players/${id}/`, payload);
export const deletePlayer = (id) => api.delete(`/api/coredata/players/${id}/`);

// 2026-05-30 — Mobile lookup. Companion to Player self-signup —
// scorer hits this before quick-create when they type a phone number;
// if it matches an existing Player (claimed or not), the modal offers
// "Attach existing" instead of creating a duplicate row.
// 200: { id, first_name, last_name, mobile, is_claimed, ... }
// 404: { detail: "No player found for this mobile." }
export const findPlayerByMobile = (mobile) =>
  api.get(`/api/coredata/players/by-mobile/`, { params: { mobile } });

// T263 — Quick Player bulk create. lines is an array of strings —
// each line is "Name", "Name, 7", or "7 - Name". Returns created
// Player rows with minimal fields filled.
export const quickCreatePlayers = ({ lines, organizationId = null }) =>
  api.post(`/api/coredata/players/quick-create/`, {
    lines,
    ...(organizationId ? { organization_id: organizationId } : {}),
  });

// T260 — Claim a Quick Player record by token.
export const claimPlayer = (claimToken) =>
  api.post(`/api/coredata/players/claim/${claimToken}/`);

// T262 — Player profile (canonical, self-edited).
export const getMyPlayerProfile = () =>
  api.get(`/api/coredata/player-profile/me/`);
export const updateMyPlayerProfile = (patch) =>
  api.put(`/api/coredata/player-profile/me/`, patch);
export const uploadMyPlayerPhoto = (file) => {
  const fd = new FormData();
  fd.append("photo", file);
  return api.post(`/api/coredata/player-profile/me/photo/`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// T262 — Public find-a-player marketplace (no auth).
export const findPlayers = (filters = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== "" && v != null) params.set(k, v);
  }
  const qs = params.toString();
  return api.get(`/api/coredata/find-a-player/${qs ? "?" + qs : ""}`);
};
export const getPublicPlayer = (id) =>
  api.get(`/api/coredata/find-a-player/${id}/`);

// T266 — Scout-gated contact unlock. Authenticated; 402 PAYMENT_REQUIRED
// when the user lacks an active B2B_SCOUT subscription.
export const getPlayerContact = (id) =>
  api.get(`/api/coredata/find-a-player/${id}/contact/`);

// TEAMS
export const listTeams = (page = 1, opts = {}) =>
  api.get(listUrl(`/api/coredata/teams/`, page, opts));
export const getTeam = (id) => api.get(`/api/coredata/teams/${id}/`);
export const createTeam = (payload) =>
  api.post(`/api/coredata/teams/`, payload);
export const updateTeam = (id, payload) =>
  api.put(`/api/coredata/teams/${id}/`, payload);
export const deleteTeam = (id) => api.delete(`/api/coredata/teams/${id}/`);

// STADIUMS
export const listStadiums = (page = 1, opts = {}) =>
  api.get(listUrl(`/api/coredata/stadiums/`, page, opts));
export const getStadium = (id) => api.get(`/api/coredata/stadiums/${id}/`);
export const createStadium = (payload) =>
  api.post(`/api/coredata/stadiums/`, payload);
export const updateStadium = (id, payload) =>
  api.put(`/api/coredata/stadiums/${id}/`, payload);
export const deleteStadium = (id) =>
  api.delete(`/api/coredata/stadiums/${id}/`);

// UMPIRES
export const listUmpires = (page = 1, opts = {}) =>
  api.get(listUrl(`/api/coredata/umpires/`, page, opts));
export const getUmpire = (id) => api.get(`/api/coredata/umpires/${id}/`);
export const createUmpire = (payload) =>
  api.post(`/api/coredata/umpires/`, payload);
export const updateUmpire = (id, payload) =>
  api.put(`/api/coredata/umpires/${id}/`, payload);
export const deleteUmpire = (id) => api.delete(`/api/coredata/umpires/${id}/`);

// NATIONALITIES — read-only lookup; used to resolve nationality UUIDs to names
export const listNationalities = () =>
  api.get(`/api/coredata/nationalities/`);
